import type { Brand } from '@studio/models/device';
import { isNil, isNotNil } from '@tryiris-ai/js-types';
import { fetchModels } from '#~/api/device/registry/index.js';
import { depot } from '#~/depot/index.js';

/**
 * Parallel registry reads the catalogue prefetch keeps in flight. Small on purpose: the warm-up
 * shares the cloud connection with the user's own reads (`getModels`, `getTemplate`), which must
 * keep winning.
 */
export const PrefetchConcurrency = 2;

/**
 * Brands this session has already asked the registry for through the prefetch path — in flight
 * or settled, succeeded or failed. One request per brand per session, whatever re-renders or
 * re-queues happen (a failed brand simply contributes no combined rows; the user's own
 * `useModels` read still reports its error if they pick that brand).
 */
const requested = new Set<Brand['id']>();

/** Brands still waiting for a worker, best first. Replaced wholesale by every call. */
let queue: Brand['id'][] = [];

/** Workers currently draining `queue`. */
let active = 0;

/** Token source of the latest call, so a long-lived worker never reuses a stale getter. */
let getToken: () => Promise<string> = () => Promise.reject(new Error('prefetch not started'));

/** Whether another path (`getModels`, an earlier worker) already cached this brand. */
const isCached = (brandId: Brand['id']): boolean =>
  isNotNil(depot.getState().registry.models[brandId]);

/**
 * One worker: takes the best remaining brand, fetches it, writes it into the depot, repeats until
 * the queue is empty. Deliberately NOT `mutex.runExclusive` (see `getModels`): queued behind the
 * shared registry mutex, a whole-catalogue warm-up would delay every user-initiated read behind
 * hundreds of brands. Writing the result the same way `getModels` does keeps that command
 * cache-first for brands the prefetch reached.
 */
const drain = async (): Promise<void> => {
  active += 1;

  try {
    for (let next = queue.shift(); next !== undefined; next = queue.shift()) {
      // `const` so the depot closure below keeps the narrowed type.
      const brandId = next;

      // Re-checked at dequeue time: another path may have cached it while it waited.
      if (requested.has(brandId) || isCached(brandId)) {
        continue;
      }

      requested.add(brandId);

      try {
        const result = await fetchModels(brandId, await getToken());

        depot.getState().produce((draft) => {
          // A user read that overtook us already wrote an equal collection; keep that reference
          // so memoised readers do not re-render for nothing.
          if (isNil(draft.registry.models[brandId])) {
            draft.registry.models[brandId] = result;
          }
        });
      } catch {
        // Best-effort warm-up: nothing to tell the user (they did not ask for this brand) and no
        // retry (see `requested`). The user's own reads surface registry errors.
      }
    }
  } finally {
    active -= 1;
  }
};

/**
 * Warm the registry model cache for the given brands, best first, so the brand field can offer
 * combined "Brand · Model" rows (ENG-4831 B3) from whatever is cached as the user types.
 *
 * Each call replaces the pending order (callers re-prioritise as the query changes) but never
 * re-requests a brand this session already asked for, and never requests a brand that is already
 * cached. Returns immediately; results land in `depot.registry.models`.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — a separate, capped lane for speculative
 * reads instead of flooding the shared mutex that user-initiated reads wait on.
 *
 * @param brandIds - Brands to warm, in priority order.
 * @param getAccessToken - Access token source (called per request; the auth SDK caches).
 */
export const prefetchModels = (
  brandIds: readonly Brand['id'][],
  getAccessToken: () => Promise<string>
): void => {
  getToken = getAccessToken;
  queue = brandIds.filter((brandId) => !requested.has(brandId) && !isCached(brandId));

  // `drain` claims its first brand synchronously, so each iteration sees the updated queue.
  while (active < PrefetchConcurrency && queue.length > 0) {
    void drain();
  }
};
